import { useCallback, useEffect, useState } from "react";
import { FileInput, Pencil, Plus, Trash2, ArrowUp, ArrowDown, RotateCcw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const FIELD_TYPES = [
  { id: "text", de: "Textfeld", en: "Text field" },
  { id: "textarea", de: "Mehrzeiliger Text", en: "Multiline text" },
  { id: "select", de: "Auswahlfeld", en: "Select field" },
];

function FieldEditor({ field, idx, total, onChange, onMove, onRemove }) {
  const { t } = useLang();
  const set = (patch) => onChange({ ...field, ...patch });
  const setLabel = (lang, v) => set({ label: { ...field.label, [lang]: v } });
  const setOption = (i, patch) => set({ options: field.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
  const setOptionLabel = (i, lang, v) => setOption(i, { label: { ...(field.options[i].label || {}), [lang]: v } });

  return (
    <div className="rounded-[12px] border border-border p-4 space-y-3 bg-muted/20" data-testid={`form-field-row-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <Badge variant="secondary" className="rounded-full text-[10px]">{t("Feld", "Field")} {idx + 1} / {total}</Badge>
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-[8px]" onClick={() => onMove(-1)} data-testid={`form-field-up-${idx}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-[8px]" onClick={() => onMove(1)} data-testid={`form-field-down-${idx}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-[8px] text-destructive" onClick={onRemove} data-testid={`form-field-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">{t("Feld-Key (technisch)", "Field key (technical)")}</Label>
          <Input value={field.key} onChange={(e) => set({ key: e.target.value.replace(/[^a-z0-9_]/g, "_") })} className="rounded-[10px] h-9 text-sm font-mono-num" data-testid={`form-field-key-${idx}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("Typ", "Type")}</Label>
          <Select value={field.type} onValueChange={(v) => set({ type: v, options: v === "select" ? (field.options?.length ? field.options : [{ value: "option_1", label: { de: "", en: "" } }]) : [] })}>
            <SelectTrigger className="rounded-[10px] h-9" data-testid={`form-field-type-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((ft) => <SelectItem key={ft.id} value={ft.id}>{t(ft.de, ft.en)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("Pflichtfeld", "Required")}</Label>
          <div className="h-9 flex items-center">
            <Switch checked={field.required !== false} onCheckedChange={(v) => set({ required: !!v })} data-testid={`form-field-required-${idx}`} />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">{t("Beschriftung (DE)", "Label (DE)")}</Label>
          <Input value={field.label?.de || ""} onChange={(e) => setLabel("de", e.target.value)} className="rounded-[10px] h-9 text-sm" data-testid={`form-field-label-de-${idx}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("Beschriftung (EN)", "Label (EN)")}</Label>
          <Input value={field.label?.en || ""} onChange={(e) => setLabel("en", e.target.value)} className="rounded-[10px] h-9 text-sm" data-testid={`form-field-label-en-${idx}`} />
        </div>
      </div>
      {field.type === "select" && (
        <div className="space-y-2">
          <Label className="text-xs">{t("Optionen", "Options")}</Label>
          {(field.options || []).map((o, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`form-option-row-${idx}-${i}`}>
              <Input placeholder="wert" value={o.value} onChange={(e) => setOption(i, { value: e.target.value.replace(/[^a-z0-9_]/g, "_") })} className="rounded-[8px] h-8 text-xs font-mono-num" data-testid={`form-option-value-${idx}-${i}`} />
              <Input placeholder="Label DE" value={o.label?.de || ""} onChange={(e) => setOptionLabel(i, "de", e.target.value)} className="rounded-[8px] h-8 text-xs" data-testid={`form-option-de-${idx}-${i}`} />
              <Input placeholder="Label EN" value={o.label?.en || ""} onChange={(e) => setOptionLabel(i, "en", e.target.value)} className="rounded-[8px] h-8 text-xs" data-testid={`form-option-en-${idx}-${i}`} />
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-[8px] text-destructive" onClick={() => set({ options: field.options.filter((_, j) => j !== i) })} data-testid={`form-option-remove-${idx}-${i}`}><Trash2 className="h-3 w-3" /></Button>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" className="rounded-[8px] h-8 text-xs" onClick={() => set({ options: [...(field.options || []), { value: `option_${(field.options || []).length + 1}`, label: { de: "", en: "" } }] })} data-testid={`form-option-add-${idx}`}>
            <Plus className="h-3 w-3 mr-1" /> {t("Option hinzuf\u00fcgen", "Add option")}
          </Button>
        </div>
      )}
    </div>
  );
}

function FormEditDialog({ def, onOpenChange, onSaved }) {
  const { t } = useLang();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (def) setForm(JSON.parse(JSON.stringify({ title: def.title, intro: def.intro, fields: def.fields, active: def.active })));
  }, [def]);

  if (!def || !form) return null;

  const moveField = (idx, dir) => {
    const next = [...form.fields];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setForm({ ...form, fields: next });
  };

  const save = async () => {
    for (const f of form.fields) {
      if (!f.key.trim()) return toast.error(t("Jedes Feld braucht einen Key", "Every field needs a key"));
      if (!f.label?.de?.trim()) return toast.error(t(`Feld '${f.key}': deutsche Beschriftung fehlt`, `Field '${f.key}': German label missing`));
      if (f.type === "select" && !(f.options || []).length) return toast.error(t(`Auswahlfeld '${f.key}' braucht Optionen`, `Select field '${f.key}' needs options`));
    }
    try {
      setBusy(true);
      const res = await api.put(`/cms/forms/${def.type}`, form);
      toast.success(t("Formular gespeichert \u2013 \u00c4nderungen sind live", "Form saved \u2013 changes are live"));
      onSaved(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Fehler beim Speichern", "Error saving"));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    try {
      setBusy(true);
      const res = await api.post(`/cms/forms/${def.type}/reset`);
      toast.success(t("Auf Standard zur\u00fcckgesetzt", "Reset to default"));
      onSaved(res.data);
      onOpenChange(false);
    } catch {
      toast.error(t("Fehler", "Error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!def} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="form-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{t("Formular bearbeiten", "Edit form")}: {def.type}</DialogTitle>
          <DialogDescription>{t("\u00c4nderungen wirken direkt auf das \u00f6ffentliche Formular", "Changes apply directly to the public form")} (/anfrage/{def.type})</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">{t("Titel (DE)", "Title (DE)")}</Label>
              <Input value={form.title.de || ""} onChange={(e) => setForm({ ...form, title: { ...form.title, de: e.target.value } })} className="rounded-[10px]" data-testid="form-title-de" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">{t("Titel (EN)", "Title (EN)")}</Label>
              <Input value={form.title.en || ""} onChange={(e) => setForm({ ...form, title: { ...form.title, en: e.target.value } })} className="rounded-[10px]" data-testid="form-title-en" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">{t("Einleitung (DE)", "Intro (DE)")}</Label>
              <Textarea rows={3} value={form.intro.de || ""} onChange={(e) => setForm({ ...form, intro: { ...form.intro, de: e.target.value } })} className="rounded-[10px]" data-testid="form-intro-de" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">{t("Einleitung (EN)", "Intro (EN)")}</Label>
              <Textarea rows={3} value={form.intro.en || ""} onChange={(e) => setForm({ ...form, intro: { ...form.intro, en: e.target.value } })} className="rounded-[10px]" data-testid="form-intro-en" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">{t("Formularfelder (Schritt 1)", "Form fields (step 1)")}</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-[10px]" onClick={() => setForm({ ...form, fields: [...form.fields, { key: `feld_${form.fields.length + 1}`, type: "text", required: true, label: { de: "", en: "" }, options: [] }] })} data-testid="form-add-field">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> {t("Feld hinzuf\u00fcgen", "Add field")}
              </Button>
            </div>
            {form.fields.map((f, idx) => (
              <FieldEditor
                key={idx}
                field={f}
                idx={idx}
                total={form.fields.length}
                onChange={(nf) => setForm({ ...form, fields: form.fields.map((x, i) => (i === idx ? nf : x)) })}
                onMove={(dir) => moveField(idx, dir)}
                onRemove={() => setForm({ ...form, fields: form.fields.filter((_, i) => i !== idx) })}
              />
            ))}
          </div>
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="ghost" className="rounded-[10px] text-muted-foreground" disabled={busy} onClick={reset} data-testid="form-reset">
            <RotateCcw className="h-4 w-4 mr-1.5" /> {t("Standard wiederherstellen", "Restore default")}
          </Button>
          <Button className="rounded-[10px] bg-[#0B1220]" disabled={busy} onClick={save} data-testid="form-save">
            {busy ? t("Speichert\u2026", "Saving\u2026") : t("Speichern & live schalten", "Save & go live")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminForms() {
  const { t, tt } = useLang();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/cms/forms").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const onSaved = (updated) => setItems((prev) => (prev || []).map((f) => (f.type === updated.type ? updated : f)));

  return (
    <div className="space-y-6" data-testid="admin-forms-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">{t("Formular-Editor", "Form Editor")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("Felder, Beschriftungen und Optionen der \u00f6ffentlichen Anfrage-Formulare bearbeiten", "Edit fields, labels and options of the public inquiry forms")}</p>
      </div>

      {!items ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-[16px]" />)}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((f) => (
            <Card key={f.type} className="rounded-[16px] card-hover" data-testid={`form-def-${f.type}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="h-10 w-10 rounded-[12px] bg-[#0B1220] flex items-center justify-center shrink-0">
                    <FileInput className="h-5 w-5 text-[#D6C08D]" />
                  </div>
                  <Badge variant="secondary" className="rounded-full text-[10px]">v{f.version}</Badge>
                </div>
                <h3 className="mt-4 text-sm font-semibold">{tt(f.title)}</h3>
                <p className="mt-1 text-xs text-muted-foreground">/anfrage/{f.type} {"\u00b7"} {f.fields.length} {t("Felder", "fields")}</p>
                <Button size="sm" variant="outline" className="mt-4 w-full rounded-[10px]" onClick={() => setEditing(f)} data-testid={`form-def-edit-${f.type}`}>
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> {t("Bearbeiten", "Edit")}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <FormEditDialog def={editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={onSaved} />
    </div>
  );
}
